import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  DollarSign,
  BarChart2,
  Activity,
  PieChart,
  Loader,
  AlertCircle,
  Search,
  ArrowUpRight,
  ArrowDownRight,
  ArrowLeftRight,
  Minus,
  ChevronRight,
  ShoppingCart,
  Filter,
} from 'lucide-react';
import { getDre, getDadosDrePlanilha } from '../../api';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
  ReferenceLine,
} from 'recharts';

// ─── Utilitários ──────────────────────────────────────────────────────────────

const formatBRL = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value) || 0);

const formatBRLCompact = (value) => {
  const n = Number(value) || 0;
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `R$ ${(n / 1_000).toFixed(1)}K`;
  return formatBRL(n);
};

const normalizar = (str) =>
  (str || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

const isPercentual = (descricao) => (descricao || '').includes('%');

const formatPercent = (value) => {
  const n = Number(value) * 100;
  return `${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
};

const calcVar = (atual, anterior) => {
  if (!anterior || anterior === 0) return null;
  return ((atual - anterior) / Math.abs(anterior)) * 100;
};

// ─── Constantes ───────────────────────────────────────────────────────────────

const MESES = [
  { value: 1, label: 'Jan', labelFull: 'Janeiro' },
  { value: 2, label: 'Fev', labelFull: 'Fevereiro' },
  { value: 3, label: 'Mar', labelFull: 'Março' },
  { value: 4, label: 'Abr', labelFull: 'Abril' },
  { value: 5, label: 'Mai', labelFull: 'Maio' },
  { value: 6, label: 'Jun', labelFull: 'Junho' },
  { value: 7, label: 'Jul', labelFull: 'Julho' },
  { value: 8, label: 'Ago', labelFull: 'Agosto' },
  { value: 9, label: 'Set', labelFull: 'Setembro' },
  { value: 10, label: 'Out', labelFull: 'Outubro' },
  { value: 11, label: 'Nov', labelFull: 'Novembro' },
  { value: 12, label: 'Dez', labelFull: 'Dezembro' },
];

const QUARTERS = [
  { value: 'Q1', label: 'Q1', meses: [1, 2, 3], labelFull: 'Q1 (Jan–Mar)' },
  { value: 'Q2', label: 'Q2', meses: [4, 5, 6], labelFull: 'Q2 (Abr–Jun)' },
  { value: 'Q3', label: 'Q3', meses: [7, 8, 9], labelFull: 'Q3 (Jul–Set)' },
  { value: 'Q4', label: 'Q4', meses: [10, 11, 12], labelFull: 'Q4 (Out–Dez)' },
];

const anoAtual = new Date().getFullYear();
const ANOS = Array.from({ length: anoAtual - 2021 + 2 }, (_, i) => 2021 + i);

const KPIS_CONFIG = [
  {
    key: 'faturamento_bruto',
    titulo: 'Fat. Bruto',
    descricao: 'Total bruto faturado',
    termos: ['faturamento bruto', 'receita bruta'],
    cor: '#7C3AED',
    corFundo: '#EDE9FE',
    Icone: DollarSign,
  },
  {
    key: 'faturamento_liquido',
    titulo: 'Fat. Líquido',
    descricao: 'Após deduções de vendas',
    termos: ['faturamento liquido', 'receita liquida'],
    cor: '#2563EB',
    corFundo: '#DBEAFE',
    Icone: TrendingUp,
  },
  {
    key: 'lucro_bruto',
    titulo: 'Lucro Bruto',
    descricao: 'Receita menos CPV',
    termos: ['lucro bruto'],
    cor: '#16A34A',
    corFundo: '#DCFCE7',
    Icone: BarChart2,
  },
  {
    key: 'ebitda',
    titulo: 'EBITDA',
    descricao: 'Resultado operacional',
    termos: ['ebitda'],
    cor: '#D97706',
    corFundo: '#FEF3C7',
    Icone: Activity,
  },
  {
    key: 'resultado_liquido',
    titulo: 'Resultado Líquido',
    descricao: 'Lucro/prejuízo final',
    termos: ['resultado liquido'],
    cor: '#DB2777',
    corFundo: '#FCE7F3',
    Icone: PieChart,
  },
  {
    key: 'vendas',
    titulo: 'Vendas',
    descricao: 'Total de vendas realizadas',
    termos: ['vendas'],
    excluir: ['deducoes'],
    cor: '#0D9488',
    corFundo: '#CCFBF1',
    Icone: ShoppingCart,
  },
  {
    key: 'pipeline',
    titulo: 'Pipeline',
    descricao: 'Total de pipeline de vendas',
    termos: ['pipeline'],
    cor: '#0D9488',
    corFundo: '#CCFBF1',
    Icone: Filter,
  },
];

const KPIS_PCT_RESUMO = [
  {
    key: 'fat_liquido_pct',
    titulo: 'Fat. Líquido %',
    descricao: 'Fat. Líquido / Fat. Bruto',
    cor: '#2563EB',
    corFundo: '#DBEAFE',
    Icone: TrendingUp,
    calcular: (dados) => {
      const fl = buscarValorMonetario(dados, ['faturamento liquido', 'receita liquida']);
      const fb = buscarValorMonetario(dados, ['faturamento bruto', 'receita bruta']);
      if (!fb || fb === 0) return null;
      return fl / fb;
    },
  },
  {
    key: 'lucro_bruto_pct',
    titulo: 'Lucro Bruto %',
    descricao: 'Lucro Bruto / Fat. Líquido',
    cor: '#16A34A',
    corFundo: '#DCFCE7',
    Icone: BarChart2,
    calcular: (dados) => {
      const lb = buscarValorMonetario(dados, ['lucro bruto']);
      const fl = buscarValorMonetario(dados, ['faturamento liquido', 'receita liquida']);
      if (!fl || fl === 0) return null;
      return lb / fl;
    },
  },
  {
    key: 'ebitda_pct',
    titulo: 'Ebitda %',
    descricao: 'Ebitda / Fat. Líquido',
    cor: '#D97706',
    corFundo: '#FEF3C7',
    Icone: Activity,
    calcular: (dados) => {
      const ebitda = buscarValorMonetario(dados, ['ebitda']);
      const fl = buscarValorMonetario(dados, ['faturamento liquido', 'receita liquida']);
      if (!fl || fl === 0) return null;
      return ebitda / fl;
    },
  },
];

const DESTAQUES = [
  { termos: ['faturamento bruto', 'receita bruta'], cor: '#7C3AED', fundo: '#F5F3FF' },
  { termos: ['faturamento liquido', 'receita liquida'], cor: '#2563EB', fundo: '#EFF6FF' },
  { termos: ['lucro bruto'], cor: '#16A34A', fundo: '#F0FDF4' },
  { termos: ['ebitda'], cor: '#D97706', fundo: '#FFFBEB' },
  { termos: ['resultado liquido'], cor: '#DB2777', fundo: '#FDF2F8' },
  { termos: ['vendas'], excluir: ['deducoes'], cor: '#0D9488', fundo: '#F0FDFA' },
  { termos: ['pipeline'], cor: '#4F46E5', fundo: '#EEF2FF' },
];

const INDICADORES_CONFIG = [
  {
    key: 'margem_liquida',
    titulo: 'Margem Líquida',
    descricao: 'Resultado Líq. / Fat. Líquido',
    cor: '#0891B2',
    corFundo: '#E0F2FE',
    Icone: TrendingUp,
    calcular: (dados) => {
      const rl = buscarValorMonetario(dados, ['resultado liquido']);
      const fl = buscarValorMonetario(dados, ['faturamento liquido', 'receita liquida']);
      if (!fl || fl === 0) return null;
      return rl / fl;
    },
  },
  {
    key: 'eficiencia_backoffice',
    titulo: 'Efic. Backoffice',
    descricao: 'SG&A / Fat. Líquido',
    cor: '#6D28D9',
    corFundo: '#EDE9FE',
    Icone: Activity,
    calcular: (dados) => {
      const m = extrairMetricasGrafico(dados);
      const sga = m.despMaoDeObra + m.despesas;
      if (!m.fatLiquido || m.fatLiquido === 0) return null;
      return sga / m.fatLiquido;
    },
  },
  {
    key: 'conversao_ebitda',
    titulo: 'Conversão LB → EBITDA',
    descricao: 'EBITDA / Lucro Bruto',
    cor: '#059669',
    corFundo: '#D1FAE5',
    Icone: BarChart2,
    calcular: (dados) => {
      const ebitda = buscarValorMonetario(dados, ['ebitda']);
      const lb = buscarValorMonetario(dados, ['lucro bruto']);
      if (!lb || lb === 0) return null;
      return ebitda / lb;
    },
  },
  {
    key: 'aliquota_efetiva',
    titulo: 'Alíquota Efetiva',
    descricao: '(Impostos + Ded.) / Rec. Bruta',
    cor: '#DC2626',
    corFundo: '#FEE2E2',
    Icone: PieChart,
    calcular: (dados) => {
      const impostos = buscarValorMonetario(dados, ['impostos']) || 0;
      const deducoes = buscarValorMonetario(dados, ['deducoes de vendas', 'deducoes']) || 0;
      const rb = buscarValorMonetario(dados, ['faturamento bruto', 'receita bruta']);
      if (!rb || rb === 0) return null;
      return (impostos + deducoes) / rb;
    },
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function obterDestaque(descricao) {
  const norm = normalizar(descricao);
  return DESTAQUES.find((d) => {
    if (d.excluir && d.excluir.some((e) => norm.includes(e))) return false;
    return d.termos.some((t) => norm.includes(t));
  }) || null;
}

function extrairKpiDeLista(lista, termos, excluir = []) {
  const item = lista.find((d) => {
    const norm = normalizar(d.descricao);
    if (excluir.some((e) => norm.includes(e))) return false;
    return termos.some((t) => norm.includes(t));
  });
  return item ? Number(item.valor) : null;
}

// Descrições que representam um snapshot/média mensal (não um fluxo acumulável) —
// ao agregar vários meses, devem ser calculadas pela média, não pela soma.
const DESCRICOES_MEDIA = ['pipeline'];

function ehMediaMensal(descricao) {
  const norm = normalizar(descricao);
  return DESCRICOES_MEDIA.some((t) => norm.includes(t));
}

function somarPorDescricao(lista) {
  const mapa = {};
  const contagem = {};
  lista.forEach(({ descricao, valor }) => {
    const key = descricao;
    mapa[key] = (mapa[key] || 0) + Number(valor);
    contagem[key] = (contagem[key] || 0) + 1;
  });
  return Object.entries(mapa).map(([descricao, valor]) => ({
    descricao,
    valor: ehMediaMensal(descricao) ? valor / contagem[descricao] : valor,
  }));
}

// Busca valor monetário excluindo linhas que são % (para usar como denominador/numerador)
function buscarValorMonetario(dados, termos) {
  const item = dados.find((d) => {
    if (isPercentual(d.descricao)) return false;
    const norm = normalizar(d.descricao);
    return termos.some((t) => norm.includes(t));
  });
  return item ? Number(item.valor) : null;
}

function extrairMetricasGrafico(dados) {
  const sem = dados.filter((d) => !isPercentual(d.descricao));

  const pegar = (termos, excluir = []) => {
    const item = sem.find((d) => {
      const n = normalizar(d.descricao);
      if (excluir.some((e) => n.includes(e))) return false;
      return termos.some((t) => n.includes(t));
    });
    return item ? Number(item.valor) : 0;
  };

  const maoDeObra = pegar(['salarios e encargos'], ['sg&a']);

  const custos = ['servicos terceirizados', 'marketing e publicidade', 'viagens', 'administrativas gerais', 'equipamentos e materiais']
    .map((t) => pegar([t], ['sg&a']))
    .reduce((a, b) => a + b, 0);

  const despMaoDeObra = sem
    .filter((d) => { const n = normalizar(d.descricao); return n.includes('sg&a') && n.includes('salarios'); })
    .reduce((acc, d) => acc + Number(d.valor), 0);

  const despesas = sem
    .filter((d) => { const n = normalizar(d.descricao); return n.includes('sg&a') && !n.includes('salarios') && n !== 'sg&a'; })
    .reduce((acc, d) => acc + Number(d.valor), 0);

  return {
    vendas:        pegar(['vendas'], ['deducoes']),
    fatLiquido:    pegar(['faturamento liquido', 'receita liquida']),
    maoDeObra,
    custos,
    lucroBruto:    pegar(['lucro bruto']),
    despMaoDeObra,
    despesas,
    ebitda:        pegar(['ebitda']),
  };
}

// Recalcula os campos percentuais com base nos valores monetários do mesmo período
function calcularPercentuais(dados) {
  const fatLiquido = buscarValorMonetario(dados, ['faturamento liquido', 'receita liquida']);
  if (!fatLiquido) return dados;

  const lucroBruto      = buscarValorMonetario(dados, ['lucro bruto']);
  const ebitda          = buscarValorMonetario(dados, ['ebitda']);
  const resultadoLiq    = buscarValorMonetario(dados, ['resultado liquido']);

  return dados.map((d) => {
    const norm = normalizar(d.descricao);
    if (norm.includes('% margem bruta') && lucroBruto !== null)
      return { ...d, valor: lucroBruto / fatLiquido };
    if (norm.includes('% ebitda') && ebitda !== null)
      return { ...d, valor: ebitda / fatLiquido };
    if ((norm.includes('% margem liquida') || norm.includes('% margem líquida')) && resultadoLiq !== null)
      return { ...d, valor: resultadoLiq / fatLiquido };
    return d;
  });
}

function labelPeriodo(tipo, { mes, mesInicio, mesFim, ano, quarter }) {
  if (tipo === 'mes') {
    const ini = mesInicio ?? mes;
    const fim = mesFim ?? mes;
    const mI = MESES.find((m) => m.value === Number(ini));
    const mF = MESES.find((m) => m.value === Number(fim));
    const a2 = String(ano).slice(-2);
    if (ini === fim) return `${mF?.label || fim}/${a2}`;
    return `${mI?.label}→${mF?.label}/${a2}`;
  }
  if (tipo === 'quarter') {
    return `${quarter}/${String(ano).slice(-2)}`;
  }
  return String(ano);
}

// ─── Variação Badge ───────────────────────────────────────────────────────────

function VarBadge({ atual, anterior }) {
  const v = calcVar(atual, anterior);
  if (v === null) return <span style={{ color: '#9CA3AF', fontSize: 11 }}>—</span>;
  const positivo = v >= 0;
  const zero = Math.abs(v) < 0.05;
  const cor = zero ? '#000000' : positivo ? '#16A34A' : '#DC2626';
  const Icon = zero ? Minus : positivo ? ArrowUpRight : ArrowDownRight;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 11, fontWeight: 600, color: cor, whiteSpace: 'nowrap', flexShrink: 0 }}>
      <Icon size={11} />
      {zero ? '0,0%' : `${positivo ? '+' : ''}${v.toFixed(1)}%`}
    </span>
  );
}

// Variação em pontos percentuais (p.p.) para campos que são %
function VarBadgePP({ atual, anterior }) {
  if (atual === null || anterior === null)
    return <span style={{ color: '#9CA3AF', fontSize: 11 }}>—</span>;
  const diff = (Number(atual) - Number(anterior)) * 100; // já são frações → ×100 = p.p.
  const zero = Math.abs(diff) < 0.01;
  const positivo = diff > 0;
  const cor = zero ? '#6B7280' : positivo ? '#16A34A' : '#DC2626';
  const Icon = zero ? Minus : positivo ? ArrowUpRight : ArrowDownRight;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 11, fontWeight: 600, color: cor, whiteSpace: 'nowrap', flexShrink: 0 }}>
      <Icon size={11} />
      {`${positivo && !zero ? '+' : ''}${diff.toFixed(2).replace('.', ',')} p.p.`}
    </span>
  );
}

// ─── KPI Card Comparativo ─────────────────────────────────────────────────────

function KpiCard({ kpi, periodos }) {
  const { Icone, titulo, cor, corFundo } = kpi;
  const valores = periodos.map((p) => extrairKpiDeLista(p.dados, kpi.termos, kpi.excluir));

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
        border: '1px solid #F3F4F6',
        borderLeft: `4px solid ${cor}`,
        padding: '14px 16px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <div style={{ background: corFundo, borderRadius: 8, padding: 6, display: 'flex' }}>
          <Icone size={14} style={{ color: cor }} />
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {titulo}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {periodos.map((p, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', flexWrap: 'nowrap', gap: 6 }}>
            <span style={{ fontSize: 11, color: '#6B7280', minWidth: 40, flexShrink: 0 }}>{p.label}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: valores[i] !== null ? '#1F2937' : '#D1D5DB', whiteSpace: 'nowrap' }}>
                {valores[i] !== null ? formatBRLCompact(valores[i]) : '—'}
              </span>
              {i > 0 && <VarBadge atual={valores[0]} anterior={valores[i]} />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function KpiIndicadorCard({ indicador, periodos }) {
  const { Icone, titulo, descricao: desc, cor, corFundo } = indicador;
  const valores = periodos.map((p) => indicador.calcular(p.dados));

  const fmtPct = (v) => {
    if (v === null || v === undefined) return null;
    const n = Number(v) * 100;
    return `${n.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
  };

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
        border: '1px solid #F3F4F6',
        borderLeft: `4px solid ${cor}`,
        padding: '14px 16px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
        <div style={{ background: corFundo, borderRadius: 8, padding: 6, display: 'flex', flexShrink: 0, marginTop: 1 }}>
          <Icone size={14} style={{ color: cor }} />
        </div>
        <div>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block' }}>
            {titulo}
          </span>
          <span style={{ fontSize: 10, color: '#D1D5DB' }}>{desc}</span>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {periodos.map((p, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', flexWrap: 'nowrap', gap: 6 }}>
            <span style={{ fontSize: 11, color: '#6B7280', minWidth: 40, flexShrink: 0 }}>{p.label}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: valores[i] !== null ? cor : '#D1D5DB', whiteSpace: 'nowrap' }}>
                {valores[i] !== null ? fmtPct(valores[i]) : '—'}
              </span>
              {i > 0 && <VarBadgePP atual={valores[0]} anterior={valores[i]} />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Agrupamento DRE ─────────────────────────────────────────────────────────

const HEADERS_GRUPO = new Set([
  'faturamento bruto',
  'receita bruta',
  'faturamento liquido',
  'receita liquida',
  'custos totais',
  'lucro bruto',
  'sg&a',
  'sga',
  'ebitda',
  'despesas nao operacionais',
  'ebit',
  'despesas financeiras',
  'lair',
  'impostos',
  'resultado liquido',
  'vendas',
  'pipeline',
]);

function ehHeaderGrupo(descricao) {
  return HEADERS_GRUPO.has(normalizar(descricao));
}

function agruparLinhas(descricoes) {
  const grupos = [];
  let atual = null;
  descricoes.forEach((desc) => {
    if (ehHeaderGrupo(desc)) {
      atual = { desc, subItens: [] };
      grupos.push(atual);
    } else if (atual) {
      atual.subItens.push(desc);
    } else {
      grupos.push({ desc, subItens: [] });
    }
  });
  return grupos;
}

// Sub-itens que devem sempre ficar visíveis, sem opção de recolher
const SEMPRE_VISIVEIS_TERMOS = ['% margem bruta', '% ebitda', '% margem liquida', '% margem líquida'];

function ehSempreVisivel(desc) {
  const norm = normalizar(desc);
  return SEMPRE_VISIVEIS_TERMOS.some((t) => norm.includes(t));
}

// ─── Tabela Comparativa ───────────────────────────────────────────────────────

const TH_STYLE = {
  padding: '10px 16px',
  textAlign: 'right',
  fontSize: 11,
  fontWeight: 600,
  color: '#9CA3AF',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  whiteSpace: 'nowrap',
};

function TabelaComparativa({ periodos }) {
  // Inverte a ordem de exibição: período mais antigo à esquerda → variação mostra crescimento positivo
  const periodosExibicao = useMemo(() => [...periodos].reverse(), [periodos]);

  const [expandidos, setExpandidos] = useState(new Set());

  const toggleGrupo = (desc) => {
    setExpandidos((prev) => {
      const next = new Set(prev);
      next.has(desc) ? next.delete(desc) : next.add(desc);
      return next;
    });
  };

  const todasDescricoes = useMemo(() => {
    const set = new Set();
    periodos.forEach((p) => p.dados.forEach((d) => set.add(d.descricao)));
    return Array.from(set);
  }, [periodos]);

  const grupos = useMemo(() => agruparLinhas(todasDescricoes), [todasDescricoes]);

  if (periodos.every((p) => p.dados.length === 0)) return null;

  const getValores = (desc) =>
    periodosExibicao.map((p) => {
      const item = p.dados.find((d) => d.descricao === desc);
      return item ? Number(item.valor) : null;
    });

  const CelulasValor = ({ desc, tamanho = 13, paddingV = '10px' }) => {
    const ehPerc = isPercentual(desc);
    const destaque = obterDestaque(desc);
    const fmt = (v) => (ehPerc ? formatPercent(v) : formatBRL(v));
    const vals = getValores(desc);
    return (
      <>
        {vals.map((v, i) => (
          <td key={i} style={{ padding: `${paddingV} 16px`, textAlign: 'right', whiteSpace: 'nowrap' }}>
            <span style={{ fontSize: tamanho, fontWeight: destaque ? 600 : 400, color: destaque ? destaque.cor : '#374151' }}>
              {v !== null ? fmt(v) : <span style={{ color: '#D1D5DB' }}>—</span>}
            </span>
          </td>
        ))}
        {periodos.length >= 2 && (
          <td style={{ padding: `${paddingV} 16px`, textAlign: 'right' }}>
            {ehPerc
              ? <VarBadgePP atual={vals[vals.length - 1]} anterior={vals[0]} />
              : <VarBadge atual={vals[vals.length - 1]} anterior={vals[0]} />
            }
          </td>
        )}
      </>
    );
  };

  return (
    <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.07)', border: '1px solid #F3F4F6', overflow: 'hidden' }}>
      <div style={{ padding: '16px 24px', borderBottom: '1px solid #F3F4F6' }}>
        <h2 style={{ fontSize: 14, fontWeight: 600, color: '#374151', margin: 0 }}>Demonstrativo Comparativo</h2>
        <p style={{ fontSize: 12, color: '#9CA3AF', margin: '2px 0 0' }}>
          {periodosExibicao.map((p) => p.label).join(' → ')}
        </p>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #F3F4F6' }}>
              <th style={{ padding: '10px 24px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Descrição
              </th>
              {periodosExibicao.map((p, i) => (
                <th key={i} style={TH_STYLE}>{p.label}</th>
              ))}
              {periodos.length >= 2 && <th style={TH_STYLE}>Var %</th>}
            </tr>
          </thead>
          <tbody>
            {grupos.map(({ desc, subItens }) => {
              const destaque = obterDestaque(desc);
              const isAberto = expandidos.has(desc);

              // Separa sub-itens que devem ficar sempre visíveis dos recolhíveis
              const sempreVisiveis = subItens.filter(ehSempreVisivel);
              const recolhiveis = subItens.filter((s) => !ehSempreVisivel(s));
              const temSub = recolhiveis.length > 0;

              const renderSubLinha = (subDesc, si) => {
                const subDest = obterDestaque(subDesc);
                return (
                  <tr
                    key={subDesc}
                    style={{
                      backgroundColor: subDest ? subDest.fundo : (si % 2 === 0 ? '#FAFAFA' : '#F5F5F5'),
                      borderBottom: '1px solid #F5F5F5',
                    }}
                  >
                    <td style={{ padding: '8px 24px 8px 52px' }}>
                      {subDest ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 2, height: 14, borderRadius: 2, background: subDest.cor, flexShrink: 0 }} />
                          <span style={{ fontWeight: 600, color: subDest.cor, fontSize: 12 }}>{subDesc}</span>
                        </div>
                      ) : (
                        <span style={{ color: '#6B7280', fontSize: 12 }}>{subDesc}</span>
                      )}
                    </td>
                    <CelulasValor desc={subDesc} tamanho={12} paddingV="8px" />
                  </tr>
                );
              };

              return (
                <React.Fragment key={desc}>
                  {/* ── Linha de grupo (header) ── */}
                  <tr
                    style={{
                      backgroundColor: destaque ? destaque.fundo : '#FFFFFF',
                      borderBottom: '1px solid #F0F0F0',
                      cursor: temSub ? 'pointer' : 'default',
                    }}
                    onClick={() => temSub && toggleGrupo(desc)}
                  >
                    <td style={{ padding: '11px 24px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {temSub && (
                          <span style={{
                            color: destaque ? destaque.cor : '#9CA3AF',
                            display: 'inline-flex',
                            transform: isAberto ? 'rotate(90deg)' : 'rotate(0deg)',
                            transition: 'transform 0.15s',
                          }}>
                            <ChevronRight size={14} />
                          </span>
                        )}
                        {destaque ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 3, height: 18, borderRadius: 2, background: destaque.cor, flexShrink: 0 }} />
                            <span style={{ fontWeight: 700, color: destaque.cor, fontSize: 13 }}>{desc}</span>
                          </div>
                        ) : (
                          <span style={{ fontWeight: temSub ? 600 : 400, color: '#1F2937', fontSize: 13 }}>{desc}</span>
                        )}
                        {temSub && !isAberto && (
                          <span style={{ fontSize: 10, color: '#9CA3AF', background: '#F3F4F6', borderRadius: 10, padding: '1px 7px' }}>
                            {recolhiveis.length}
                          </span>
                        )}
                      </div>
                    </td>
                    <CelulasValor desc={desc} />
                  </tr>

                  {/* ── Sub-itens sempre visíveis (sem opção de recolher) ── */}
                  {sempreVisiveis.map((subDesc, si) => renderSubLinha(subDesc, si))}

                  {/* ── Sub-itens recolhíveis (visíveis quando expandido) ── */}
                  {isAberto && recolhiveis.map((subDesc, si) => renderSubLinha(subDesc, si))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Componente Principal ─────────────────────────────────────────────────────

const CORES_PERIODO = ['#7C3AED', '#DB2777', '#D97706', '#16A34A'];

// ─── Gráfico Comparativo ──────────────────────────────────────────────────────

const CATS_GRAFICO = [
  { key: 'vendas',        label: 'Vendas' },
  { key: 'fatLiquido',    label: 'Faturamento\nLíquido' },
  { key: 'maoDeObra',     label: '(-) Mão\nde Obra' },
  { key: 'custos',        label: '(-) Custos' },
  { key: 'lucroBruto',    label: 'Lucro\nBruto' },
  { key: 'despMaoDeObra', label: '(-) Desp.\nMão de Obra' },
  { key: 'despesas',      label: '(-) Despesas' },
  { key: 'ebitda',        label: 'EBITDA' },
];

const CORES_GRAFICO = ['#7A1A3E', '#C97090', '#D97706', '#16A34A'];

function CustomXAxisTick({ x, y, payload }) {
  return (
    <g transform={`translate(${x},${y})`}>
      {payload.value.split('\n').map((line, i) => (
        <text key={i} x={0} y={0} dy={14 + i * 13} textAnchor="middle" fill="#6B7280" fontSize={10} fontWeight={700}>
          {line}
        </text>
      ))}
    </g>
  );
}

function GraficoComparativo({ periodos }) {
  if (periodos.length < 2) return null;
  if (periodos.every((p) => p.dados.length === 0)) return null;

  const periodosExibicao = [...periodos].reverse();
  const metricas = periodosExibicao.map((p) => extrairMetricasGrafico(p.dados));

  const dadosGrafico = CATS_GRAFICO.map((cat) => {
    const entry = { cat: cat.label };
    metricas.forEach((m, i) => { entry[`p${i}`] = m[cat.key]; });
    return entry;
  });

  const criarLabel = (periodoIdx) => (props) => {
    const { x, y, width, height, value, index } = props;
    if (value === undefined || value === null) return null;
    const formatted = formatBRLCompact(value);
    const acima = value >= 0;
    const labelY = acima ? y - 5 : y + Math.abs(height) + 14;

    const crescimentoEl = (() => {
      if (periodoIdx === 0) return null;
      const val0 = dadosGrafico[index]?.p0;
      if (!val0 || val0 === 0) return null;
      const pct = ((value - val0) / Math.abs(val0)) * 100;
      const positivo = pct >= 0;
      return (
        <text
          x={x + width / 2}
          y={acima ? y - 20 : y + Math.abs(height) + 30}
          textAnchor="middle"
          fill={positivo ? '#16A34A' : '#DC2626'}
          fontSize={15}
          fontWeight={700}
        >
          {positivo ? '▲' : '▼'} {Math.abs(pct).toFixed(0)}%
        </text>
      );
    })();

    return (
      <g>
        <text x={x + width / 2} y={labelY} textAnchor="middle" fill="#374151" fontSize={11} fontWeight={600}>
          {formatted}
        </text>
        {crescimentoEl}
      </g>
    );
  };

  const titulo = periodosExibicao.map((p) => p.label).join(' vs ');

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
        border: '1px solid #F3F4F6',
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: '16px 24px', borderBottom: '1px solid #F3F4F6' }}>
        <h2 style={{ fontSize: 14, fontWeight: 600, color: '#374151', margin: 0 }}>
          Análise Histórica {titulo}
        </h2>
        <p style={{ fontSize: 12, color: '#9CA3AF', margin: '2px 0 0' }}>
          Comparativo de indicadores entre períodos
        </p>
      </div>
      <div style={{ padding: '24px 16px 16px' }}>
        <div style={{ maxWidth: '80%', margin: '0 auto' }}>
        <ResponsiveContainer width="100%" height={480}>
          <BarChart
            data={dadosGrafico}
            margin={{ top: 40, right: 20, left: 10, bottom: 40 }}
            barCategoryGap="25%"
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
            <XAxis
              dataKey="cat"
              tick={<CustomXAxisTick />}
              tickLine={false}
              axisLine={false}
              interval={0}
              height={55}
            />
            <YAxis
              tickFormatter={(v) => {
                if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
                if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
                return String(v);
              }}
              tick={{ fontSize: 10, fill: '#9CA3AF' }}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              formatter={(value, name) => [formatBRL(value), name]}
              labelFormatter={(label) => label.replace(/\n/g, ' ')}
              contentStyle={{
                borderRadius: 8,
                border: '1px solid #E5E7EB',
                fontSize: 12,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}
            />
            <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 8, fontWeight: 700 }} />
            <ReferenceLine y={0} stroke="#D1D5DB" />
            {periodosExibicao.map((p, i) => (
              <Bar
                key={p.label}
                dataKey={`p${i}`}
                name={p.label}
                fill={CORES_GRAFICO[i % CORES_GRAFICO.length]}
                radius={[3, 3, 0, 0]}
              >
                <LabelList content={criarLabel(i)} />
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

// ─── Gráfico Pipeline (mensal, comparativo entre períodos) ────────────────────

// Meses considerados no gráfico de Pipeline, de acordo com o tipo de período ativo.
// Usa o período base (primeiro card) como referência de trimestre/intervalo de meses.
function mesesDoPipeline(tipoPeriodo, filtroBase) {
  if (tipoPeriodo === 'quarter') {
    const q = QUARTERS.find((q) => q.value === filtroBase.quarter);
    return q ? q.meses : QUARTERS[0].meses;
  }
  if (tipoPeriodo === 'mes') {
    const ini = Number(filtroBase.mesInicio ?? filtroBase.mes);
    const fim = Number(filtroBase.mesFim ?? filtroBase.mes);
    const arr = [];
    for (let m = ini; m <= fim; m++) arr.push(m);
    return arr;
  }
  return MESES.map((m) => m.value); // 'ano'
}

function GraficoPipeline({ allDados, periodosFiltros, tipoPeriodo }) {
  // Mesma convenção do GraficoComparativo: período mais antigo à esquerda → base à direita
  const periodosExibicao = useMemo(() => [...periodosFiltros].reverse(), [periodosFiltros]);
  const filtroBase = periodosFiltros[0];

  const mesesConsiderados = useMemo(
    () => mesesDoPipeline(tipoPeriodo, filtroBase),
    [tipoPeriodo, filtroBase.quarter, filtroBase.mesInicio, filtroBase.mesFim, filtroBase.mes]
  );
  const mostrarMedia = mesesConsiderados.length > 1;
  const labelMedia = 'Pipeline (Médio)';

  const seriesPorPeriodo = useMemo(() => {
    return periodosExibicao.map((filtro) => {
      const doAno = allDados.filter((d) => {
        const norm = normalizar(d.descricao);
        return norm.includes('pipeline') && Number(d.ano) === Number(filtro.ano);
      });

      const porMes = mesesConsiderados.map((mesVal) => {
        const item = doAno.find((d) => Number(d.mes) === mesVal);
        return item ? Number(item.valor) : null;
      });

      const validos = porMes.filter((v) => v !== null);
      const media = validos.length ? validos.reduce((a, b) => a + b, 0) / validos.length : null;

      return {
        ano: filtro.ano,
        valores: mostrarMedia ? [...porMes, media] : porMes,
        temDados: doAno.length > 0,
      };
    });
  }, [allDados, periodosExibicao, mesesConsiderados, mostrarMedia]);

  if (seriesPorPeriodo.every((s) => !s.temDados)) return null;

  const categorias = [
    ...mesesConsiderados.map((mv) => MESES.find((m) => m.value === mv)?.label || mv),
    ...(mostrarMedia ? [labelMedia] : []),
  ];
  const dadosGrafico = categorias.map((cat, idx) => {
    const entry = { cat };
    seriesPorPeriodo.forEach((s, i) => { entry[`p${i}`] = s.valores[idx]; });
    return entry;
  });

  const criarLabel = (periodoIdx) => (props) => {
    const { x, y, width, value, index } = props;
    if (value === undefined || value === null) return null;

    const crescimentoEl = (() => {
      if (periodoIdx === 0) return null;
      const val0 = dadosGrafico[index]?.p0;
      if (val0 === undefined || val0 === null || val0 === 0) return null;
      const pct = ((value - val0) / Math.abs(val0)) * 100;
      const positivo = pct >= 0;
      return (
        <text
          x={x + width / 2}
          y={y - 20}
          textAnchor="middle"
          fill={positivo ? '#16A34A' : '#DC2626'}
          fontSize={12}
          fontWeight={700}
        >
          {positivo ? '▲' : '▼'} {Math.abs(pct).toFixed(0)}%
        </text>
      );
    })();

    return (
      <g>
        <text x={x + width / 2} y={y - 6} textAnchor="middle" fill="#374151" fontSize={10} fontWeight={600}>
          {formatBRLCompact(value)}
        </text>
        {crescimentoEl}
      </g>
    );
  };

  const titulo = periodosExibicao.map((s) => String(s.ano)).join(' vs ');
  const subtitulo = tipoPeriodo === 'quarter'
    ? `Comparativo dos meses do ${filtroBase.quarter} entre os anos selecionados${mostrarMedia ? ' — última barra é a média do trimestre' : ''}`
    : tipoPeriodo === 'mes'
    ? `Comparativo entre os anos selecionados${mostrarMedia ? ' — última barra é a média do período' : ''}`
    : 'Comparativo mês a mês entre os anos selecionados — última barra é a média do ano completo';

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
        border: '1px solid #F3F4F6',
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: '16px 24px', borderBottom: '1px solid #F3F4F6' }}>
        <h2 style={{ fontSize: 14, fontWeight: 600, color: '#374151', margin: 0 }}>
          Pipeline Mensal {titulo}
        </h2>
        <p style={{ fontSize: 12, color: '#9CA3AF', margin: '2px 0 0' }}>
          {subtitulo}
        </p>
      </div>
      <div style={{ padding: '24px 16px 16px' }}>
        <div style={{ maxWidth: '90%', margin: '0 auto' }}>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={dadosGrafico}
              margin={{ top: 40, right: 20, left: 10, bottom: 10 }}
              barCategoryGap="20%"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
              <XAxis
                dataKey="cat"
                tickLine={false}
                axisLine={false}
                interval={0}
                tick={{ fontSize: 11, fill: '#6B7280', fontWeight: 700 }}
              />
              <YAxis
                tickFormatter={(v) => {
                  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
                  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
                  return String(v);
                }}
                tick={{ fontSize: 10, fill: '#9CA3AF' }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                formatter={(value, name) => [formatBRL(value), name]}
                contentStyle={{
                  borderRadius: 8,
                  border: '1px solid #E5E7EB',
                  fontSize: 12,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                }}
              />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 8, fontWeight: 700 }} />
              <ReferenceLine y={0} stroke="#D1D5DB" />
              {seriesPorPeriodo.map((s, i) => (
                <Bar
                  key={s.ano + '-' + i}
                  dataKey={`p${i}`}
                  name={String(s.ano)}
                  fill={CORES_GRAFICO[i % CORES_GRAFICO.length]}
                  radius={[3, 3, 0, 0]}
                >
                  <LabelList content={criarLabel(i)} />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

export default function Dre() {
  const navigate = useNavigate();

  const [allDados, setAllDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tipoPeriodo, setTipoPeriodo] = useState('ano'); // 'mes' | 'quarter' | 'ano'

  const mesAtual = new Date().getMonth() + 1;
  const quarterAtual = QUARTERS.find((q) => q.meses.includes(mesAtual))?.value || 'Q1';

  const periodoDefault = (offsetMes = 0) => {
    const mes = mesAtual - offsetMes;
    const mesVal = mes > 0 ? mes : mes + 12;
    const anoVal = mes > 0 ? anoAtual : anoAtual - 1;
    return {
      mesInicio: mesVal,
      mesFim: mesVal,
      ano: anoVal,
      quarter: quarterAtual,
    };
  };

  const [periodosFiltros, setPeriodosFiltros] = useState([periodoDefault(0)]);

  const buscarTodosAnos = async () => {
    setLoading(true);
    setError(null);
    try {
      const anosNecessarios = [...new Set(periodosFiltros.map((p) => p.ano))];
      const anosDB       = anosNecessarios.filter((a) => a !== 2026);
      const precisa2026  = anosNecessarios.includes(2026);

      const promessas = anosDB.map(async (ano) => {
        const params = new URLSearchParams({ ano });
        const res = await getDre(params.toString());
        return (res || []).map((d) => ({ ...d, ano: d.ano ?? ano }));
      });

      if (precisa2026) {
        promessas.push(
          getDadosDrePlanilha().then((res) =>
            (res?.data || []).map((d) => ({
              descricao: d['DESCRIÇÃO'],
              valor:     d['VALOR'],
              mes:       d['MÊS'],
              ano:       d['ANO'] ?? 2026,
            }))
          )
        );
      }

      const resultados = await Promise.all(promessas);
      setAllDados(resultados.flat());
    } catch (err) {
      setError(err.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    buscarTodosAnos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const periodos = useMemo(() => {
    return periodosFiltros.map((filtro) => {
      let dados = allDados.filter((d) => Number(d.ano) === Number(filtro.ano));

      if (tipoPeriodo === 'mes') {
        const ini = Number(filtro.mesInicio ?? filtro.mes);
        const fim = Number(filtro.mesFim ?? filtro.mes);
        dados = dados.filter((d) => { const m = Number(d.mes); return m >= ini && m <= fim; });
        if (ini !== fim) dados = somarPorDescricao(dados);
      } else if (tipoPeriodo === 'quarter') {
        const q = QUARTERS.find((q) => q.value === filtro.quarter);
        if (q) dados = dados.filter((d) => q.meses.includes(Number(d.mes)));
        dados = somarPorDescricao(dados);
      } else {
        dados = somarPorDescricao(dados);
      }

      dados = calcularPercentuais(dados);

      return { label: labelPeriodo(tipoPeriodo, filtro), filtro, dados };
    });
  }, [allDados, periodosFiltros, tipoPeriodo]);

  const atualizarPeriodo = (index, novoValor) => {
    const copia = [...periodosFiltros];
    copia[index] = novoValor;
    setPeriodosFiltros(copia);
  };

  const adicionarPeriodo = () => {
    if (periodosFiltros.length >= 4) return;
    setPeriodosFiltros([...periodosFiltros, periodoDefault(periodosFiltros.length)]);
  };

  const removerPeriodo = (index) => {
    setPeriodosFiltros(periodosFiltros.filter((_, i) => i !== index));
  };

  const swapPeriodos = () => {
    if (periodosFiltros.length < 2) return;
    const copia = [...periodosFiltros];
    [copia[0], copia[1]] = [copia[1], copia[0]];
    setPeriodosFiltros(copia);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F9FAFB' }}>
      {/* Header */}
      <header style={{
        background: 'linear-gradient(135deg, #4C1D95, #9D174D)',
        color: '#fff',
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 22, fontWeight: 700 }}>YOUX</span>
          
          <span style={{ fontSize: 20, fontWeight: 300 }}>GROUP</span>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: '0.05em' }}>DRE</div>
          <div style={{ fontSize: 11, opacity: 0.7 }}>Demonstrativo de Resultado</div>
        </div>
        <button
          onClick={() => navigate('/inicio')}
          style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
        >
          Menu Principal
        </button>
      </header>

      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 24px', display: 'flex', flexDirection: 'column', gap: 24 }}>

        {/* Painel de Configuração */}
        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.07)', border: '1px solid #F3F4F6', padding: 20 }}>

          {/* Linha 1: Tipo de período + hint */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 4, background: '#F3F4F6', borderRadius: 10, padding: 4 }}>
              {[
                { key: 'mes', label: 'Mensal' },
                { key: 'quarter', label: 'Trimestral' },
                { key: 'ano', label: 'Anual' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setTipoPeriodo(key)}
                  style={{
                    padding: '6px 18px',
                    borderRadius: 7,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    border: 'none',
                    background: tipoPeriodo === key ? 'linear-gradient(135deg, #7C3AED, #DB2777)' : 'transparent',
                    color: tipoPeriodo === key ? '#fff' : '#6B7280',
                    transition: 'all 0.15s',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {periodosFiltros.length > 1 && (
              <span style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic' }}>
                Comparando {periodosFiltros.length} períodos
              </span>
            )}
          </div>

          {/* Linha 2: Seletores de período */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
            {periodosFiltros.map((filtro, i) => {
              const cor = CORES_PERIODO[i];
              const isBase = i === 0;
              return (
                <React.Fragment key={i}>
                <div
                  style={{
                    border: `1.5px solid ${cor}22`,
                    borderRadius: 10,
                    padding: '10px 14px',
                    background: `${cor}08`,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    minWidth: 200,
                    position: 'relative',
                  }}
                >
                  {/* Badge + botão remover */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: cor,
                      background: `${cor}18`,
                      padding: '2px 8px',
                      borderRadius: 20,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                    }}>
                      {isBase ? 'Período base' : `Comparação ${i}`}
                    </span>
                    {!isBase && (
                      <button
                        onClick={() => removerPeriodo(i)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', fontSize: 16, lineHeight: 1, padding: '0 2px' }}
                        title="Remover período"
                      >
                        ×
                      </button>
                    )}
                  </div>

                  {/* Selects */}
                  <div style={{ display: 'flex', gap: 6 }}>
                    {tipoPeriodo === 'mes' && (
                      <>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
                          <span style={{ fontSize: 10, color: '#9CA3AF', fontWeight: 600, paddingLeft: 2 }}>De</span>
                          <select
                            value={filtro.mesInicio ?? filtro.mes}
                            onChange={(e) => {
                              const ini = Number(e.target.value);
                              const fim = filtro.mesFim ?? filtro.mes;
                              atualizarPeriodo(i, { ...filtro, mesInicio: ini, mesFim: Math.max(ini, fim) });
                            }}
                            style={{ padding: '6px 8px', borderRadius: 7, border: '1px solid #E5E7EB', fontSize: 13, color: '#374151', outline: 'none', background: '#fff' }}
                          >
                            {MESES.map((m) => (
                              <option key={m.value} value={m.value}>{m.labelFull}</option>
                            ))}
                          </select>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
                          <span style={{ fontSize: 10, color: '#9CA3AF', fontWeight: 600, paddingLeft: 2 }}>Até</span>
                          <select
                            value={filtro.mesFim ?? filtro.mes}
                            onChange={(e) => {
                              const fim = Number(e.target.value);
                              const ini = filtro.mesInicio ?? filtro.mes;
                              atualizarPeriodo(i, { ...filtro, mesFim: fim, mesInicio: Math.min(ini, fim) });
                            }}
                            style={{ padding: '6px 8px', borderRadius: 7, border: '1px solid #E5E7EB', fontSize: 13, color: '#374151', outline: 'none', background: '#fff' }}
                          >
                            {MESES.map((m) => (
                              <option key={m.value} value={m.value}>{m.labelFull}</option>
                            ))}
                          </select>
                        </div>
                      </>
                    )}
                    {tipoPeriodo === 'quarter' && (
                      <select
                        value={filtro.quarter}
                        onChange={(e) => atualizarPeriodo(i, { ...filtro, quarter: e.target.value })}
                        style={{ padding: '6px 8px', borderRadius: 7, border: '1px solid #E5E7EB', fontSize: 13, color: '#374151', outline: 'none', background: '#fff', flex: 1 }}
                      >
                        {QUARTERS.map((q) => (
                          <option key={q.value} value={q.value}>{q.labelFull}</option>
                        ))}
                      </select>
                    )}
                    <select
                      value={filtro.ano}
                      onChange={(e) => atualizarPeriodo(i, { ...filtro, ano: Number(e.target.value) })}
                      style={{ padding: '6px 8px', borderRadius: 7, border: '1px solid #E5E7EB', fontSize: 13, color: '#374151', outline: 'none', background: '#fff' }}
                    >
                      {ANOS.map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {i === 0 && periodosFiltros.length >= 2 && (
                  <button
                    onClick={swapPeriodos}
                    title="Inverter base ↔ comparação 1"
                    style={{
                      alignSelf: 'center',
                      background: '#fff',
                      border: '1.5px solid #E5E7EB',
                      borderRadius: '50%',
                      width: 34,
                      height: 34,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      color: '#6B7280',
                      flexShrink: 0,
                      transition: 'border-color 0.15s, color 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#7C3AED'; e.currentTarget.style.color = '#7C3AED'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.color = '#6B7280'; }}
                  >
                    <ArrowLeftRight size={14} />
                  </button>
                )}
                </React.Fragment>
              );
            })}

            {/* Botão adicionar período */}
            {periodosFiltros.length < 4 && (
              <button
                onClick={adicionarPeriodo}
                style={{
                  alignSelf: 'center',
                  padding: '8px 14px',
                  borderRadius: 10,
                  border: '1.5px dashed #D1D5DB',
                  background: 'transparent',
                  color: '#6B7280',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  transition: 'border-color 0.15s, color 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#7C3AED'; e.currentTarget.style.color = '#7C3AED'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#D1D5DB'; e.currentTarget.style.color = '#6B7280'; }}
              >
                + Comparar período
              </button>
            )}

            {/* Botão buscar */}
            <button
              onClick={buscarTodosAnos}
              disabled={loading}
              style={{
                alignSelf: 'center',
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '10px 22px',
                background: 'linear-gradient(135deg, #7C3AED, #DB2777)',
                color: '#fff',
                border: 'none',
                borderRadius: 9,
                fontSize: 13,
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
              }}
            >
              {loading ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Search size={14} />}
              Buscar
            </button>
          </div>
        </div>

        {/* Estado: loading */}
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 0', gap: 12, color: '#9CA3AF' }}>
            <Loader size={28} style={{ color: '#7C3AED', animation: 'spin 1s linear infinite' }} />
            <span style={{ fontSize: 14 }}>Carregando dados...</span>
          </div>
        )}

        {/* Estado: erro */}
        {error && !loading && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 12, padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <AlertCircle size={18} style={{ color: '#DC2626', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: '#B91C1C', margin: 0 }}>Erro ao carregar dados</p>
              <p style={{ fontSize: 12, color: '#EF4444', margin: '2px 0 0' }}>{error}</p>
            </div>
            <button
              onClick={buscarTodosAnos}
              style={{ padding: '6px 14px', background: '#FEE2E2', color: '#B91C1C', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
            >
              Tentar novamente
            </button>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Resumo */}
            <div>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0 0 10px 2px' }}>
                Resumo
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 14 }}>
                {/* Pipeline — sem percentual */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <KpiCard kpi={KPIS_CONFIG[5]} periodos={periodos} />
                  <KpiCard kpi={KPIS_CONFIG[6]} periodos={periodos} />
                </div>
                {/* Fat. Bruto — sem percentual */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <KpiCard kpi={KPIS_CONFIG[0]} periodos={periodos} />
                </div>
                {/* Fat. Líquido + Fat. Líquido % */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <KpiCard kpi={KPIS_CONFIG[1]} periodos={periodos} />
                  <KpiIndicadorCard indicador={KPIS_PCT_RESUMO[0]} periodos={periodos} />
                </div>
                {/* Lucro Bruto + Lucro Bruto % */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <KpiCard kpi={KPIS_CONFIG[2]} periodos={periodos} />
                  <KpiIndicadorCard indicador={KPIS_PCT_RESUMO[1]} periodos={periodos} />
                </div>
                {/* Ebitda + Ebitda % */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <KpiCard kpi={KPIS_CONFIG[3]} periodos={periodos} />
                  <KpiIndicadorCard indicador={KPIS_PCT_RESUMO[2]} periodos={periodos} />
                </div>
                {/* Resultado Líquido — sem percentual */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <KpiCard kpi={KPIS_CONFIG[4]} periodos={periodos} />
                </div>
              </div>
            </div>

            {/* Indicadores */}
            <div>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0 0 10px 2px' }}>
                Indicadores
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
                {INDICADORES_CONFIG.map((ind) => (
                  <KpiIndicadorCard key={ind.key} indicador={ind} periodos={periodos} />
                ))}
              </div>
            </div>

            {/* Gráfico Comparativo */}
            <GraficoComparativo periodos={periodos} />

            {/* Gráfico Pipeline (mensal) */}
            <GraficoPipeline allDados={allDados} periodosFiltros={periodosFiltros} tipoPeriodo={tipoPeriodo} />

            {/* Tabela Comparativa */}
            {periodos.some((p) => p.dados.length > 0) ? (
              <TabelaComparativa periodos={periodos} />
            ) : (
              <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #F3F4F6', padding: '60px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <BarChart2 size={36} style={{ color: '#E5E7EB' }} />
                <p style={{ color: '#9CA3AF', fontWeight: 500, margin: 0, fontSize: 14 }}>Nenhum registro encontrado</p>
                <p style={{ color: '#D1D5DB', fontSize: 12, margin: 0 }}>Ajuste os filtros e tente novamente</p>
              </div>
            )}
          </>
        )}
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}